import {get} from 'lodash-es';
import {useEffect, useState} from 'react';

import {
    TFieldWatcher,
    IFieldWatchersApplicationResult,
    IUseFormFieldsManager, IWatcher,
    EWatcherFieldFlag,
} from './use-form-fields-manager';

export interface IFieldArrayWatchersApplicationResult {
    [key: string]: IFieldWatchersApplicationResult; // key here is array index
}

export interface IUseFormListFieldsManagerProps<T, FormArrayKey> extends IUseFormFieldsManager {
    arrayIndependentWatchers?: { [key: string]: IWatcher[] };
    formArrayKey: FormArrayKey;
    modifiers?: {
        trackFormDirtyState: boolean;
        isDirty: boolean;
        setIsDirty: (v: boolean) => void;
    };
    performanceModifiers?: {
        // skips watchers reassign, потому что некоторые поля могут не быть триггером изменения watchers
        checkIfShouldSkip: (changedValues: T) => boolean;
    };
}

export const useFormListFieldsManager = <
    FormArrayKey extends string, RowType, T extends Record<FormArrayKey, RowType[]>
>({
        formInstance,
        watchers,
        triggerDeps = [],
        formArrayKey,
        arrayIndependentWatchers,
        modifiers,
        performanceModifiers,
    }: IUseFormListFieldsManagerProps<T, FormArrayKey>) => {
    const [
        watchersApplicationResult, setWatchersApplicationResult,
    ] = useState<IFieldArrayWatchersApplicationResult>({});
    const [
        arrayIndependentWatcherResult, setArrayIndependentWatcherResult,
    ] = useState<IFieldWatchersApplicationResult>({});

    const {trackFormDirtyState, setIsDirty, isDirty} = modifiers || {};

    const applyWatcher = (
        watcher: TFieldWatcher, formValues: any, fieldKey: string, applicationResult: IFieldWatchersApplicationResult,
    ) => {
        const {
            condition, flag, perform, isNotInput,
        } = watcher;
        const watcherCallbackArg = {
            value: isNotInput ? undefined : get(formValues, fieldKey),
            formValues,
            formInstance,
        };

        const isTriggered = condition(watcherCallbackArg);

        (() => { // put application result
            if (!flag) return;
            if (!applicationResult[fieldKey]) applicationResult[fieldKey] = {};
            applicationResult[fieldKey][flag] = isTriggered;
        })();

        if (isTriggered && perform) perform(watcherCallbackArg);
    };

    const applyFieldWatchers = (changedValues?: T, shouldSetDirty = true) => {
        const formValues = formInstance.getFieldsValue();

        if (trackFormDirtyState && isDirty !== true && changedValues && changedValues[formArrayKey]) {
            if (shouldSetDirty) setIsDirty?.(true);
        }

        if (performanceModifiers && changedValues) {
            if (performanceModifiers.checkIfShouldSkip(changedValues)) return;
        }

        const watchedArray: [] = formValues[formArrayKey];
        if (!watchedArray || !Array.isArray(watchedArray)) {
            return;
        }
        const collectedWatchersResultArray: IFieldArrayWatchersApplicationResult = {};
        watchedArray.forEach((arrayItemValues: any, index) => {
            const applicationResult: IFieldWatchersApplicationResult = {};

            Object.entries(watchers).forEach(([fieldKey, watchersList]) => {
                watchersList.forEach(watcher => {
                    applyWatcher(watcher, arrayItemValues, fieldKey, applicationResult);
                });
            });
            collectedWatchersResultArray[index] = applicationResult;
        });
        setWatchersApplicationResult(collectedWatchersResultArray);

        if (arrayIndependentWatchers) {
            const applicationResult: IFieldWatchersApplicationResult = {};
            Object.entries(arrayIndependentWatchers).forEach(([fieldKey, watchersList]) => {
                watchersList.forEach(watcher => {
                    applyWatcher(watcher, formValues, fieldKey, applicationResult);
                });
            });
            setArrayIndependentWatcherResult(applicationResult);
        }
    };
    useEffect(() => {
        if (triggerDeps.length) applyFieldWatchers(formInstance.getFieldsValue());
    }, [...triggerDeps]);

    const checkWatcherFlag = (
        index: number, fieldKey: string, flag: EWatcherFieldFlag,
    ) => watchersApplicationResult[index.toString()]?.[fieldKey][flag];

    const checkArrayIndependentWatcherFlag = (
        fieldKey: string, flag: EWatcherFieldFlag,
    ) => arrayIndependentWatcherResult[fieldKey]?.[flag];

    return {
        watchersApplicationResult,
        applyFieldWatchers,
        checkWatcherFlag,
        checkArrayIndependentWatcherFlag,
    };
};
